import React from "react";
import { X, Download, Printer, Lock } from "lucide-react";

const formatAppointmentDate = (dateString, timeString) => {
  if (!dateString) return "Date TBD";
  try {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    if (timeString) {
      const time = new Date(`2000-01-01T${timeString}`).toLocaleTimeString(
        "en-US",
        {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }
      );
      return `${dateStr} at ${time}`;
    }
    return dateStr;
  } catch {
    return "Invalid Date";
  }
};

const MedicalRecordModal = ({ record, onClose }) => {
  if (!record) return null;

  const doctor = record.doctorInfo || {};
  const patient = record.patientInfo || {};

  const avatar =
    doctor.photoURL ||
    doctor.photoUrl ||
    doctor.avatarUrl ||
    null;

  const created =
    record?.createdAt instanceof Date
      ? record.createdAt
      : record?.createdAt
      ? new Date(record.createdAt)
      : null;

  const downloadTxt = () => {
    const content = `
MEDICAL RECORD
==============

Patient: ${patient.name || "-"}
Doctor: ${doctor.name || "-"} • ${doctor.specialty || "-"}

Date: ${formatAppointmentDate(
      record.appointmentDate,
      record.appointmentTime
    )}

Diagnosis:
${record.diagnosis || "-"}

Symptoms:
${record.symptoms || "-"}

Notes:
${record.notes || "-"}

Prescriptions:
${(record.prescriptions || [])
  .map(
    (p) =>
      `• ${p.medication} - ${p.dosage}, ${p.frequency} for ${p.duration}
  Instructions: ${p.instructions || "-"}`
  )
  .join("\n")}

Follow-up:
${record.followUpInstructions || "-"}

Next appointment:
${record.nextAppointment || "-"}
`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Medical_Record_${record.id || "record"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printDoc = () => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html>
      <head>
        <title>Medical Record</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 24px;
            color: #111827;
            font-size: 14px;
          }
          h1 {
            font-size: 20px;
            margin: 0 0 4px 0;
          }
          .subtitle {
            font-size: 13px;
            color: #6b7280;
          }
          .section {
            margin-top: 18px;
            padding-top: 14px;
            border-top: 1px solid #e5e7eb;
          }
          .label {
            font-size: 11px;
            letter-spacing: .08em;
            text-transform: uppercase;
            color: #9ca3af;
            margin-bottom: 4px;
          }
          .value {
            white-space: pre-wrap;
          }
          .pill {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            border-radius: 999px;
            font-size: 11px;
            border: 1px solid #e5e7eb;
            color: #4b5563;
          }
        </style>
      </head>
      <body>
        <header>
          <h1>Medical Record</h1>
          <div class="subtitle">
            Patient: ${patient.name || "-"} • 
            ${formatAppointmentDate(
              record.appointmentDate,
              record.appointmentTime
            )}
          </div>
        </header>

        <section class="section">
          <div class="label">Doctor</div>
          <div class="value">
            ${doctor.name || "-"}${doctor.specialty ? " • " + doctor.specialty : ""}
          </div>
        </section>

        <section class="section">
          <div class="label">Patient</div>
          <div class="value">
            ${patient.name || "-"}
            ${patient.email ? "\\n" + patient.email : ""}
            ${patient.phone ? "\\n" + patient.phone : ""}
          </div>
        </section>

        <section class="section">
          <div class="label">Diagnosis</div>
          <div class="value">${record.diagnosis || "-"}</div>
        </section>

        <section class="section">
          <div class="label">Symptoms</div>
          <div class="value">${record.symptoms || "-"}</div>
        </section>

        <section class="section">
          <div class="label">Clinical Notes</div>
          <div class="value">${record.notes || "-"}</div>
        </section>

        <section class="section">
          <div class="label">Prescriptions</div>
          <div class="value">
            ${
              (record.prescriptions || []).length === 0
                ? "-"
                : (record.prescriptions || [])
                    .map(
                      (p) =>
                        `• ${p.medication} - ${p.dosage} (${p.frequency}, ${p.duration})${
                          p.instructions ? "\\n  " + p.instructions : ""
                        }`
                    )
                    .join("\\n\\n")
            }
          </div>
        </section>

        <section class="section">
          <div class="label">Follow-up Instructions</div>
          <div class="value">${record.followUpInstructions || "-"}</div>
        </section>

        <section class="section">
          <div class="label">Next Appointment</div>
          <div class="value">${record.nextAppointment || "-"}</div>
        </section>

      </body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.35)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          width: "100%",
          maxWidth: 720,
          maxHeight: "90vh",
          backgroundColor: "#ffffff",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 18px 45px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f9fafb",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            {avatar ? (
              <img
                src={avatar}
                alt={doctor.name || "Doctor"}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background:
                    "conic-gradient(from 180deg, #d1fae5, #bfdbfe, #fee2e2, #e5e7eb)",
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#111827",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {doctor.name || "Doctor"}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {doctor.specialty || "Specialty"} •{" "}
                {formatAppointmentDate(record.appointmentDate, record.appointmentTime)}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={downloadTxt}
              style={{
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <Download style={{ width: 14, height: 14 }} />
              TXT
            </button>
            <button
              onClick={printDoc}
              style={{
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <Printer style={{ width: 14, height: 14 }} />
              Print
            </button>
            <button
              onClick={onClose}
              style={{
                borderRadius: 999,
                border: "none",
                background: "transparent",
                padding: 6,
                cursor: "pointer",
                color: "#6b7280",
              }}
              aria-label="Close"
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            padding: "16px 18px 18px",
            overflowY: "auto",
          }}
        >
          {/* Patient + basic info */}
          <section
            style={{
              paddingBottom: 14,
              borderBottom: "1px solid #e5e7eb",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Patient
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#111827" }}>
              {patient.name || "-"}
            </div>
            {(patient.email || patient.phone) && (
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                {patient.email && <span>{patient.email}</span>}
                {patient.email && patient.phone && <span> • </span>}
                {patient.phone && <span>{patient.phone}</span>}
              </div>
            )}
          </section>

          {/* Diagnosis */}
          <section
            style={{
              padding: "10px 0 12px",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Diagnosis
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#111827",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {record.diagnosis || "-"}
            </div>
          </section>

          {/* Symptoms */}
          <section
            style={{
              padding: "10px 0 12px",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Symptoms
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {record.symptoms || "-"}
            </div>
          </section>

          {/* Clinical notes */}
          <section
            style={{
              padding: "10px 0 12px",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Clinical Notes
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {record.notes || "-"}
            </div>
          </section>

          {/* Prescriptions */}
          <section
            style={{
              padding: "10px 0 12px",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Prescriptions
            </div>
            {(record.prescriptions || []).length === 0 ? (
              <div style={{ fontSize: 14, color: "#6b7280" }}>None</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {(record.prescriptions || []).map((p, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 4,
                        color: "#111827",
                      }}
                    >
                      {p.medication}
                    </div>
                    <div style={{ fontSize: 13, color: "#4b5563" }}>
                      {p.dosage && (
                        <div>
                          <strong>Dosage:</strong> {p.dosage}
                        </div>
                      )}
                      {p.frequency && (
                        <div>
                          <strong>Frequency:</strong> {p.frequency}
                        </div>
                      )}
                      {p.duration && (
                        <div>
                          <strong>Duration:</strong> {p.duration}
                        </div>
                      )}
                      {p.instructions && (
                        <div style={{ marginTop: 4 }}>
                          <strong>Instructions:</strong> {p.instructions}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Follow-up */}
          <section
            style={{
              padding: "10px 0 12px",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Follow-up Instructions
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {record.followUpInstructions || "-"}
            </div>
          </section>

          {/* Next appointment */}
          <section
            style={{
              padding: "10px 0 0",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "#9ca3af",
                marginBottom: 4,
              }}
            >
              Next Appointment
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {record.nextAppointment || "Not scheduled"}
            </div>
          </section>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            padding: "10px 18px 12px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f9fafb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: "#9ca3af",
            }}
          >
            <Lock style={{ width: 12, height: 12 }} />
            <span>Confidential medical information</span>
          </div>
          <button
            onClick={onClose}
            style={{
              fontSize: 13,
              fontWeight: 500,
              padding: "6px 14px",
              borderRadius: 999,
              border: "1px solid #d1d5db",
              backgroundColor: "#ffffff",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MedicalRecordModal;
